import React, { useEffect, useState } from "react";
import { Col, Row, Select } from "antd";
import { Controller, useForm } from "react-hook-form";
import styles from "./add-edit-academic-year.module.css";
import CustomDrawer from "../../../../../../../../../common/custom-drawer/custom-drawer";
import CustomButton from "../../../../../../../../../common/custom-button/custom-button";
import CustomDatepicker from "../../../../../../../../../common/input/custom-datepicker/custom-datepicker";
import { ArrowRightOutlined } from "@ant-design/icons";
import CustomCheckBox from "../../../../../../../../../common/custom-checkbox/custom-checkbox";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { createAcademicYears, updateAcademicYears } from "../../../../../../../../../services/school_info";
import { validateDate } from "../../../../../../../../../utils/format-date/format-date";
import { DATE_FORMAT } from "../../../../../../../../../constants/common";
import { refetchSchool } from "../../../../../../../../../utils/common";
import CustomSelect from "../../../../../../../../../common/input/custom-select/custom-select";

const { Option } = Select;

const AddEditAcademicYear = ({ open, onClose, data, existingYears }) => {
  const [loading, setLoading] = useState(false);
  const { control, getValues, reset, trigger } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      start_date: "",
      end_date: "",
      academic_year_id: "",
      active_year: false,
    },
  });

  const yearOptions = Array.from({ length: 10 }, (_, i) => dayjs().year() + i)
    ?.filter((y) => !existingYears.includes(y))
    ?.map((y) => ({ label: y, value: y }));

  const isStartDatePast = data?.start_date
    ? dayjs(data?.start_date, DATE_FORMAT.DD_MM_YYYY).isBefore(dayjs(), "day")
    : false;

  useEffect(() => {
    if (data?.id) {
      reset({
        name: data?.name,
        start_date: validateDate(data?.start_date, DATE_FORMAT.DD_MM_YYYY),
        end_date: validateDate(data?.end_date, DATE_FORMAT.DD_MM_YYYY),
        academic_year_id: data?.academic_year_id,
        active_year: data?.active_year,
      });
    } else {
      reset({
        name: "",
        start_date: "",
        end_date: "",
        academic_year_id: "",
        active_year: false,
      });
    }
  }, [data]);

  const handleSubmit = async () => {
    try {
      const isValid = await trigger();
      if (!isValid) {
        return;
      }

      const formValues = getValues();
      const payload = {
        name: formValues?.name,
        start_date: dayjs(formValues?.start_date).format(DATE_FORMAT.DD_MM_YYYY),
        end_date: dayjs(formValues?.end_date).format(DATE_FORMAT.DD_MM_YYYY),
        academic_year_id: formValues?.academic_year_id,
        active_year: formValues?.active_year,
      }
      if (data?.id) await updateAcademicYears(data?.id, payload);
      else await createAcademicYears(payload);
      toast.success(`Academic Year ${data?.id ? "edited" : "created"} successfully!`);
      refetchSchool();
      handleClose();
    } catch (err) {
      toast.error(err?.data?.message || `Failed to ${data?.id ? "edit" : "create"} academic year`);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const disablePastDates = (current) => {
    return current && current?.isBefore(dayjs().startOf("day"));
  };

  return (
    <CustomDrawer
      title={`${data?.id ? 'Edit' : 'Add New'} Academic Year`}
      open={open}
      onClose={handleClose}
      className={styles.drawer}
      footer={
        <div className={styles.footerWrapper}>
          <CustomButton
            onClick={handleClose}
            title="Back"
            variant="outlined"
            className={styles.footerBackButton}
          />
          <CustomButton
            title={`${data?.id ? "Edit" : "Add New"} Academic Year`}
            size="large"
            onClick={handleSubmit}
          />
        </div>
      }
      width="40vw"
      drawerStyles={{ body: { padding: "50px" } }}
    >
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomSelect
                id="name"
                label="Academic Year"
                showRequired
                labelColor="#171717"
                placeholder="Enter Academic Year"
                size="large"
                type="number"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
                disabled={!!data?.id}
              >
                {yearOptions?.map((option) => (
                  <Option key={option?.value} value={option?.value}>
                    {option?.label}
                  </Option>
                ))}
              </CustomSelect>
            )}
          />
        </Col>

        <Col span={24} className={styles.dateRangeWrapper}>
          <Row gutter={[12, 0]} align="bottom">
            <Col span={11} className={styles.dateInput}>
              <Controller
                name="start_date"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <CustomDatepicker
                    id="start_date"
                    label="Duration"
                    showRequired
                    labelColor="#171717"
                    placeholder="Start Date"
                    size="large"
                    allowClear={false}
                    value={value}
                    onChange={onChange}
                    disabled={isStartDatePast}
                    style={{ height: "40px", width: "100%" }}
                    status={error ? "error" : ""}
                    prefix={
                      <img
                        src="/assets/images/calendarOutlined.svg"
                        style={{ marginRight: 4 }}
                        alt="calendar"
                      />
                    }
                  />
                )}
              />
            </Col>
            <Col span={2} className={styles.dateArrow}
            >
              <ArrowRightOutlined style={{ fontSize: 18 }} />
            </Col>
            <Col span={11}  className={styles.dateInput}>
              <Controller
                name="end_date"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <CustomDatepicker
                    id="end_date"
                    labelColor="#171717"
                    placeholder="End Date"
                    size="large"
                    allowClear={false}
                    value={value}
                    onChange={onChange}
                    disabledDate={disablePastDates}
                    style={{ height: "40px", width: "100%" }}
                    status={error ? "error" : ""}
                    prefix={
                      <img
                        src="/assets/images/calendarOutlined.svg"
                        style={{ marginRight: 4 }}
                        alt="calendar"
                      />
                    }
                  />
                )}
              />
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <label
            style={{
              fontWeight: 500,
              marginBottom: 12,
              display: "inline-block",
            }}
          >
            Is Active
          </label>
          <Controller
            name="active_year"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CustomCheckBox
                id="active_year"
                label="Active"
                showOptional="(Mark if the year being created is the current or active academic year)"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                errorMessage={error?.message}
              />
            )}
          />
        </Col>
      </Row>
    </CustomDrawer>
  );
};

export default AddEditAcademicYear;
